import { collectionSchema } from "@workspace/public-seo";
import { Link } from "wouter";
import { SeoHead } from "@/components/SeoHead";
import { Calendar, Tag } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface GuideArticle {
  id: number;
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  scheduledAt: string | null;
  tags: string[];
  coverImage: string;
  coverImageAlt: string;
  readingMinutes: number;
}

/** Format a date for display: accepts ISO datetime or YYYY-MM-DD, returns 2025年1月6日 */
function formatChineseDate(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) {
    // Fallback: parse YYYY-MM-DD text directly
    const [y, m, day] = value.split("-").map(Number);
    return `${y}年${m}月${day}日`;
  }
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}年${m}月${day}日`;
}

export default function GuidesPage() {
  const { data: articles = [], isLoading } = useQuery<GuideArticle[]>({
    queryKey: ["guides"],
    queryFn: async () => {
      const res = await fetch("/api/guides");
      if (!res.ok) throw new Error("Failed to load articles");
      return res.json();
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="採購知識中心 — 香港學校招標採購實務文章"
        description="每週更新的香港學校採購與招標實務文章，涵蓋採購金額界線、招標流程、常見問題等，助學校行政人員及供應商掌握最新採購規範。"
        canonical="/guides"
        structuredData={collectionSchema("/guides", "採購指南", articles.map(a => ({ name: a.title, path: `/guides/${encodeURIComponent(a.slug)}` })))}
      />

      {/* Hero */}
      <div className="border-b bg-secondary/30 py-10">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <p className="text-xs font-medium text-primary uppercase tracking-widest mb-2">採購知識中心</p>
          <h1 className="text-2xl md:text-3xl font-bold mb-2 leading-snug">學校採購實務指南</h1>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">
            每週更新，為香港中小學行政人員及供應商提供最新的招標採購實務知識、法規解析與操作技巧。
          </p>
        </div>
      </div>

      {/* Article list */}
      <div className="container mx-auto px-4 md:px-8 py-10 max-w-4xl">
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2">
            {[1, 2].map((i) => (
              <div key={i} className="rounded-xl border bg-card overflow-hidden animate-pulse">
                <div className="bg-muted" style={{ aspectRatio: "1200/630" }} />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded" />
                  <div className="h-4 bg-muted rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2">
            {articles.map((article) => (
              <Link key={article.slug} href={`/guides/${article.slug}`}>
                <article className="group rounded-xl border bg-card overflow-hidden hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col">
                  {/* Cover */}
                  {article.coverImage && (
                    <div className="relative overflow-hidden" style={{ aspectRatio: "1200/630" }}>
                      <img
                        src={article.coverImage}
                        alt={article.coverImageAlt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        width={1200}
                        height={630}
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Body */}
                  <div className="p-5 flex flex-col flex-1 space-y-3">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5">
                      {(article.tags ?? []).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-0.5"
                        >
                          <Tag className="w-2.5 h-2.5" />
                          {tag}
                        </span>
                      ))}
                    </div>

                    <h2 className="font-bold text-base leading-snug group-hover:text-primary transition-colors">
                      {article.title}
                    </h2>

                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3 flex-1">
                      {article.description}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatChineseDate(article.scheduledAt ?? article.publishedAt)}
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
